import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCartContainer from './Containers/MiniCart.container';
import QuickSearchContainer from './Containers/QuickSearch.container';
import NavigationContainer from './Containers/Navigation.container';
import CategoryLinkContainer from './Containers/CategoryLink.container';
import FacetedSearchContainer from './Containers/FacetedSearch.container';
import FacetedSearchCompactContainer from './Containers/FacetedSearchCompact.container';
import DynamicComponent from './Components/DynamicComponent';
import { customScripts } from './wooscripts/scripts';
import { wooAccordionScripts } from './wooscripts/woo-accordions';
import { pushToDatalayer } from './wooscripts/gtmEnchanced';
import SizeGuide from './Components/SizeGuide';
import SizeGuideButton from './Components/SizeGuideButton';
import { filterCustomScripts } from './wooscripts/filter-scripts';
import { productSliderScripts } from './wooscripts/product-slider';
import { navMobileExtra } from './wooscripts/navigation-mobile-extras';
import ChangePasswordByToken from './Components/ChangePasswordByToken';
import RegisterMeContainer from './Containers/RegisterMe.container';
import QuickBuy from './Components/QuickBuy';
import CasallProQuickBuy from './Components/CasallPro/CasallProQuickBuy';

window.onload = function () {
    customScripts();
    filterCustomScripts();
    wooAccordionScripts();
    productSliderScripts();
    navMobileExtra();
};

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
// use the parent page's store if possible, so the iframe will share the same store with the parent context.
// this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
// when reorder action happens.
const store = (window.__litium.store =
    window.__litium.store ||
    (window.parent.__litium && window.parent.__litium.store) ||
    createStore(
        app,
        preloadState,
        composeWithDevTools(applyMiddleware(thunk, historyMiddleware)),
    ));
window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach(container => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCartContainer />
            </Provider>,
            document.getElementById('miniCart'),
        );
    }
    if (document.getElementById('sizeGuide')) {
        renderReact(
            <Provider store={store}>
                <SizeGuide />
            </Provider>,
            document.getElementById('sizeGuide'),
        );
    }
    if (document.getElementById('sizeGuideButton')) {
        renderReact(
            <Provider store={store}>
                <SizeGuideButton />
            </Provider>,
            document.getElementById('sizeGuideButton'),
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearchContainer />
            </Provider>,
            document.getElementById('quickSearch'),
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <NavigationContainer />
            </Provider>,
            document.getElementById('navbar'),
        );
    }
    if (document.getElementById('categoryLink')) {
        renderReact(
            <Provider store={store}>
                <CategoryLinkContainer />
            </Provider>,
            document.getElementById('categoryLink'),
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchContainer />
            </Provider>,
            document.getElementById('facetedSearch'),
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact'),
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Containers/PersonList.container'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons'),
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Containers/AddressList.container'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses'),
        );
    }
    if (document.querySelectorAll('.newsletterBlock').length > 0) {
        const NewsletterContainer = DynamicComponent({
            loader: () => import('./Containers/Newsletter.container'),
        });

        Array.from(document.querySelectorAll('.newsletterBlock'))
            .filter(x => x.dataset.payload != 'null')
            .forEach((item, i) => {
                renderReact(
                    <NewsletterContainer
                        payload={JSON.parse(item.dataset.payload)}
                        namespace={`newsletter-${i}`}
                    />,
                    item,
                );
            });
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Containers/Checkout.container'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout'),
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Containers/LightboxImages.container'),
        });
        const rootElement = document.getElementById('lightBoxImages');
        const media = Array.from(rootElement.querySelectorAll('a media'));

        renderReact(
            <Provider store={store}>
                <LightboxImages media={media} />
            </Provider>,
            document.getElementById('lightBoxImages'),
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [
                    ...slider.querySelectorAll('.slider__block'),
                ].map(block => {
                    return {
                        image: block.dataset.image,
                        url: block.dataset.url,
                        text: block.dataset.text,
                    };
                });
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            },
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Containers/BuyButton.Container'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(button => {
            const { articleNumber, quantityFieldId, href, cssClass, label } =
                button.dataset;
            renderReact(
                <Provider store={store}>
                    <BuyButton
                        {...{
                            label,
                            articleNumber,
                            quantityFieldId,
                            href,
                            cssClass,
                        }}
                    />
                </Provider>,
                button,
            );
        });
    }

    if (document.querySelectorAll('reorder-button').length > 0) {
        const ReorderButton = DynamicComponent({
            loader: () => import('./Containers/ReorderButton.Container'),
        });
        Array.from(document.querySelectorAll('reorder-button')).forEach(
            button => {
                const { cssClass, orderId, title } = button.dataset;
                const label = button.innerText;
                renderReact(
                    <Provider store={store}>
                        <ReorderButton
                            {...{ label, title, cssClass, orderId }}
                        />
                    </Provider>,
                    button,
                );
            },
        );
    }

    if (document.querySelectorAll('gtm-tag').length > 0) {
        Array.from(document.querySelectorAll('gtm-tag')).forEach(gtm => {
            if (gtm.dataset.click) {
                gtm.addEventListener('click', function (event) {
                    pushToDatalayer(gtm.dataset);
                });
            } else {
                pushToDatalayer(gtm.dataset);
            }
        });
    }

    if (document.getElementById('returnsRoot')) {
        const Returns = DynamicComponent({
            loader: () => import('./Components/Returns'),
        });
        renderReact(<Returns />, document.getElementById('returnsRoot'));
    }

    if (document.querySelectorAll('.progress-circle').length > 0) {
        const ProgressBar = DynamicComponent({
            loader: () => import('./Components/CircularProgressBar'),
        });
        Array.from(document.querySelectorAll('.progress-circle')).forEach(
            progressCircle => {
                const dataSet = progressCircle.dataset;
                const htmlcontent = progressCircle.innerHTML;
                renderReact(
                    <Provider store={store}>
                        <ProgressBar {...{ ...dataSet, htmlcontent }} />
                    </Provider>,
                    progressCircle,
                );
            },
        );
    }

    const registerMe = Array.from(document.querySelectorAll('.registerMe'));
    if (registerMe.length > 0) {
        registerMe.forEach(registerMe => {
            if (registerMe) {
                ReactDOM.render(
                    <Provider store={store}>
                        <RegisterMeContainer {...registerMe.dataset} />
                    </Provider>,
                    registerMe,
                );
            }
        });
    }
    
    if (document.querySelectorAll('.login').length > 0) {
        const Login = DynamicComponent({
            loader: () => import('./Components/Login'),
        });
        Array.from(document.querySelectorAll('.login')).forEach(login => {
            renderReact(<Login {...login.dataset} />, login);
        });
    }

    if (document.querySelectorAll('#quickBuy').length > 0) {
        Array.from(document.querySelectorAll('#quickBuy')).forEach(
            (button) => {
                let productAvailableColors = button.getAttribute('data-availableColors')
                renderReact(
                    <Provider store={store}>
                        <QuickBuy
                            productAvailableColors={JSON.parse(productAvailableColors)}
                            gtmTrackingData={button.dataset}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.querySelectorAll('#casallProQuickBuy').length > 0) {
        Array.from(document.querySelectorAll('#casallProQuickBuy')).forEach(
            (button) => {
                let productAvailableColors = button.getAttribute('data-availableColors')
                let productType = button.getAttribute('data-productType')
                let productId = button.getAttribute('data-productId')
                let loggedOutText = button.getAttribute('data-loggedOutText')
                let offert = button.getAttribute('data-offert')
                renderReact(
                    <Provider store={store}>
                        <CasallProQuickBuy
                            productAvailableColors={JSON.parse(productAvailableColors)}
                            gtmTrackingData={button.dataset}
                            producttype={productType}
                            productid={productId}
                            loggedouttext={loggedOutText}
                            offert={offert}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }
    
    if (document.getElementById('forgotPassword')) {
        const ForgotPassword = DynamicComponent({
            loader: () => import('./Components/ForgotPassword'),
        });
        const element = document.getElementById('forgotPassword');
        renderReact(<ForgotPassword 
            title={element.dataset.title} 
            loginpageurl={element.dataset.loginpageurl} 
            redirecturl = {element.dataset.redirecturl}/>, element);
    }

    if (document.getElementById('changePasswordByToken')) {
        const ChangePasswordByToken = DynamicComponent({
            loader: () => import('./Components/ChangePasswordByToken'),
        });
        const element = document.getElementById('changePasswordByToken');
        renderReact(<ChangePasswordByToken 
            title={element.dataset.title} 
            token={element.dataset.token}
            haspresetpassword={element.dataset.haspresetpassword}
            redirecturl={element.dataset.redirecturl} />, element);
    }

    if (document.getElementById('multipleSizeInputComponent')) {
        const PurchaseMultiSizeInputComponent = DynamicComponent({
            loader: () => import('./Containers/PurchaseMultiSizeInput.container'),
        });

        const element = document.getElementById('multipleSizeInputComponent');
        renderReact(
            <Provider store={store}>
                <PurchaseMultiSizeInputComponent {...element.dataset} />
            </Provider>, 
            element
        );
    }

    if (document.getElementById('casallProMultipleSizeInputComponent')) {
        const CasallProPurchaseMultiSizeInputComponent = DynamicComponent({
            loader: () => import('./Containers/CasallPro/CasallProPurchaseMultiSizeInput.container'),
        });

        const element = document.getElementById('casallProMultipleSizeInputComponent');

        renderReact(
            <Provider store={store}>
                <CasallProPurchaseMultiSizeInputComponent {...element.dataset} />
            </Provider>, 
            element
        );
    }

    if (document.querySelectorAll('.quotation-form').length > 0) {
        const QuotationForm = DynamicComponent({
          loader: () => import('./Containers/CasallPro/Quotation/QuotationForm.container'),
        });
        Array.from(document.querySelectorAll('.quotation-form')).forEach(element => {
          renderReact(
              <Provider store={store}>
                  <QuotationForm termsUrl={element.dataset.termsUrl} />
            </Provider>,
            element,
          );
        });
      }
};

bootstrapComponents();
